/**
 * Turn the baked habitat blob into meshes the renderer can register.
 *
 * The blob is written by `scripts/bake-habitat.mjs` out of the Fusion 360 CAD,
 * and `habKit.ts` beside it is the generated index: which module owns which
 * byte ranges, and which of the three shading groups it has. This file is the
 * only thing that reads them together.
 *
 * FETCHED, NOT INLINED, for the same reason the environment probes are — see
 * `bake-env.mjs`. Three megabytes of base64 in a TS module crashes esbuild's
 * transform service outright.
 *
 * NULL IS A VALID ANSWER. If the fetch fails the caller falls back to the
 * procedural builders in `habitat.ts`, which are kept for exactly this. The
 * base then looks like it did before rather than not existing, which is the
 * same posture `loadDetailTextures` takes when its art is missing.
 */

import type { MeshData } from './mesh';
import { HAB_KIT, HAB_KIT_BYTES, HAB_KIT_STRIDE, HAB_KIT_URL } from './habKit';

/**
 * Suffix per shading group, as used in the KEYS of `HabKitMeshes.meshes`.
 *
 * This is the map's own convention and is not the renderer's slot naming: a
 * module with no painted surface gives its bare slot name to whichever group it
 * does have, so `hab_leg` registers the metal. Keep the two apart — looking a
 * mesh up by its slot name is how boot broke with "Cannot read properties of
 * undefined (reading 'vertices')".
 */
export const HAB_GROUP_SUFFIX: Record<string, string> = {
  paint: '', metal: '_metal', glass: '_glass', glow: '_glow',
  lamp: '_lamp', cabin: '_cabin',
};

export interface HabKitMeshes {
  /** Renderer slot name -> geometry. Keys are `hab_core`, `hab_core_glass`, … */
  meshes: Map<string, MeshData>;
  /** Module name -> which groups it actually has, for registration. */
  groups: Map<string, string[]>;
}

/**
 * Decode the blob.
 *
 * Every offset in the index is a byte offset into this one buffer and every
 * block is 4-byte aligned by construction — positions, normals, uv and the
 * material tag are all f32, and indices are u32 — so the typed-array views can
 * be taken directly rather than copied. `slice` rather than `subarray` on the
 * vertices only because `registerMesh` holds onto them.
 */
export function buildHabKit(data: ArrayBuffer): HabKitMeshes {
  const meshes = new Map<string, MeshData>();
  const groups = new Map<string, string[]>();

  for (const entry of HAB_KIT) {
    const have: string[] = [];
    for (const [gname, g] of Object.entries(entry.groups)) {
      if (!g) continue;
      const vertices = new Float32Array(
        data.slice(g.vOff, g.vOff + g.vCount * HAB_KIT_STRIDE * 4),
      );
      const indices = new Uint32Array(data.slice(g.iOff, g.iOff + g.iCount * 4));
      meshes.set(entry.name + HAB_GROUP_SUFFIX[gname], { vertices, indices });
      have.push(gname);
    }
    groups.set(entry.name, have);
  }
  return { meshes, groups };
}

/**
 * Fetch the blob, or null.
 *
 * Eight seconds and then give up, matching `loadDetailTextures` and the
 * environment probes: `fetch` does not time out on its own, and a request that
 * never settles parks boot on a phase message forever. A hang has to degrade
 * like a failure, because to the player it is one.
 */
export async function fetchHabKit(): Promise<ArrayBuffer | null> {
  try {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 8000);
    const res = await fetch(HAB_KIT_URL, { cache: 'force-cache', signal: ctl.signal });
    clearTimeout(timer);
    if (!res.ok) throw new Error(String(res.status));
    const buf = await res.arrayBuffer();
    if (buf.byteLength < HAB_KIT_BYTES) throw new Error(`short: ${buf.byteLength}`);
    return buf;
  } catch (err) {
    console.warn('[abyss] habitat kit unavailable, using procedural modules', err);
    return null;
  }
}
